import React, { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Select } from 'glints-aries';
import { debounce, noop } from 'lodash-es';
import { defineMessage, defineMessages, useIntl } from 'react-intl';

import { countryCodeAndNameMap, CountryCodes } from 'src/common/enums';
import { unaccentTerm } from 'src/common/helpers';
import { useDeepCompareEffect } from 'src/common/hooks';
import { inputTestId, optionTestId } from 'src/common/testIds';
import { buildSelectedLocationLabel } from 'src/common/utils/hierarchicalLocationHelpers';
import {
  HierarchicalLocation,
  searchHierarchicalLocation,
  SearchHierarchicalLocationData,
} from 'src/modules/Profile/graphql/hierarchicalLocations';

const messages = defineMessages({
  placeholder: {
    id: 'location.dropdown-placeholder',
    defaultMessage: 'Enter your location (City & Country)',
  },
});

export function buildLocationWithLabel(location: HierarchicalLocation) {
  return {
    ...location,
    label: buildSelectedLocationLabel(location),
  };
}

export interface HierarchicalLocationOption extends HierarchicalLocation {
  label: string;
}

export type Props = React.ComponentPropsWithoutRef<typeof Select> & {
  value: string;
  searchLocationLevels: number[];
  autoSelectOptionIds?: string[];
  label?: React.ReactNode;
  required?: boolean;
  error?: React.ReactNode | string | boolean;
  countryCode: CountryCodes;
  onInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Enables Playwright test selectors. When set, the Select input gets
   * `data-testid="${testIdPrefix}-input"` and each option gets
   * `data-testid="${testIdPrefix}-option-${location.id}"`. Convention
   * documented in src/common/testIds.ts.
   */
  testIdPrefix?: string;
  optionProps: {
    value?: string;
    onSelectOption?: (locations: HierarchicalLocationOption[]) => void;
    onOptionClick?: (
      e: React.ChangeEvent<HTMLInputElement>,
      location: HierarchicalLocation
    ) => void;
    onBlur?: () => void;
  };
};

export const HierarchicalLocationSelect: React.FC<Props> = ({
  value,
  searchLocationLevels,
  autoSelectOptionIds,
  label,
  required,
  error,
  countryCode,
  onInputChange,
  testIdPrefix,
  optionProps = {},
  onClearSelected,
  ...selectProps
}) => {
  const {
    onOptionClick,
    onSelectOption,
    onBlur = noop,
    ...restOptionProps
  } = optionProps;

  const [fetchCities, { loading: isLoading }] =
    useLazyQuery<SearchHierarchicalLocationData>(searchHierarchicalLocation);
  const [cities, setCities] = React.useState([] as HierarchicalLocation[]);
  const locations = cities.map(buildLocationWithLabel);

  const intl = useIntl();

  const appendL2Locations = useCallback(function appendL2Locations(
    locations: HierarchicalLocation[]
  ) {
    return locations
      .flatMap((location) => [location])
      .filter(
        (location, index, self) =>
          index === self.findIndex((t) => t.id === location.id)
      )
      .sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
  }, []);

  React.useEffect(() => {
    const getCities = async () => {
      const result = await fetchCities({
        variables: {
          countryCode,
          searchTerm: countryCodeAndNameMap[countryCode],
          levels: searchLocationLevels,
        },
      });
      if (result?.data?.searchHierarchicalLocations) {
        // TODO: remove this workaround once searchHierarchicalLocations is migrated to V2
        const searchHierarchicalLocations = appendL2Locations(
          result?.data?.searchHierarchicalLocations
        );
        setCities(searchHierarchicalLocations);
      }
    };
    getCities();
  }, [appendL2Locations, searchLocationLevels, countryCode, fetchCities]);

  useDeepCompareEffect(
    function autoSelectDefaultOptions() {
      if (autoSelectOptionIds && onSelectOption && cities?.length > 0) {
        const defaultOption = cities.filter((city) =>
          autoSelectOptionIds.includes(city.id)
        );
        if (defaultOption?.length > 0) {
          onSelectOption(defaultOption.map(buildLocationWithLabel));
        }
      }
    },
    [cities, autoSelectOptionIds]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = unaccentTerm(event.target.value);
      const result = await fetchCities({
        variables: {
          countryCode,
          searchTerm,
          levels: searchLocationLevels,
        },
      });
      if (result?.data?.searchHierarchicalLocations) {
        // TODO: remove this workaround once searchHierarchicalLocations is migrated to V2
        const searchHierarchicalLocations = appendL2Locations(
          result?.data?.searchHierarchicalLocations
        );

        setCities(searchHierarchicalLocations);
        if (!(searchHierarchicalLocations?.length > 0)) {
          onClearSelected?.();
        }
      }
      if (onInputChange) {
        onInputChange(event);
      }
    }, 350),
    [onInputChange]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(event);
  };

  const makeHandleOptionClickWithLocationData: (
    location: HierarchicalLocationOption
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void = (location) => (e) => {
    if (onOptionClick) {
      onOptionClick(e, location);
    }
  };

  const noResultsFoundMessage = defineMessage({
    id: 'no.results.found',
    defaultMessage: 'No results found',
  });

  return (
    <Select
      type="text"
      id="location"
      name="location"
      value={value}
      noOptionResult={intl.formatMessage(noResultsFoundMessage)}
      // TODO: Remove when the label type is changed to ReactNode in glints-aries
      // @ts-ignore
      label={
        label || `${intl.formatMessage(messages.placeholder)}${required && '*'}`
      }
      required={required}
      error={error}
      onInputChange={handleInputChange}
      isLoading={isLoading}
      data-testid={inputTestId(testIdPrefix)}
      /**
       * Because the options have been filtered in backend and we also need to avoid
       * the default filterFunction filtering the accented location option out e.g. "Hồ Xá"
       */
      filterFunction={() => true}
      onBlur={onBlur}
      {...selectProps}
    >
      {locations.map((loc) => {
        return (
          <Select.Option
            value={loc.label}
            key={loc.id}
            onOptionClick={makeHandleOptionClickWithLocationData(loc)}
            data-testid={optionTestId(testIdPrefix, loc.id)}
            {...restOptionProps}
          >
            {/* TODO: Remove normalization after Aries option is able to show accented letters
                https://github.com/glints-dev/glints-aries/issues/444 */}
            {loc.label}
          </Select.Option>
        );
      })}
    </Select>
  );
};
